import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { formatFileSize } from '../../../../utils/fileUtils';
import {
  StorageUsageState,
  SubscriptionInfo,
} from '../../../../models/batchProjectUpload';

interface SelectedProjectData {
  folderPath: string;
  projectName: string;
  totalSize: number;
}

interface StorageUsageBannerProps {
  subscription?: SubscriptionInfo | null;
  usage: StorageUsageState;
  totalSelectedSize: number;
  selectedProjects?: SelectedProjectData[];
  onProjectClick?: (folderPath: string) => void;
}

const StorageUsageBanner: React.FC<StorageUsageBannerProps> = ({
  subscription,
  usage,
  totalSelectedSize,
  selectedProjects = [],
  onProjectClick,
}) => {
  const theme = useTheme();
  const t = theme.palette.studio;
  const segmentColors = [
    t.colors.accentFill,
    t.colors.cyan,
    t.colors.green,
    t.colors.amber,
    t.text.secondary,
  ];
  if (!subscription) {
    return null;
  }

  const {
    currentStorageUsed,
    storageLimit,
    availableStorage,
    storageAfterUpload,
    storagePercentageAfterUpload,
    wouldExceedLimit,
    isApproachingLimit,
  } = usage;

  // Calculate percentages for the bar
  const currentPercentage =
    storageLimit > 0 ? (currentStorageUsed / storageLimit) * 100 : 0;

  const afterPercentage = Math.min(storagePercentageAfterUpload, 100);
  const availablePercentage = Math.max(0, 100 - afterPercentage);

  // Calculate positions for each project segment
  const projectSegments = selectedProjects.map((project, index) => {
    const startPercentage =
      currentPercentage +
      (selectedProjects
        .slice(0, index)
        .reduce((sum, p) => sum + p.totalSize, 0) /
        storageLimit) *
        100;
    const widthPercentage = (project.totalSize / storageLimit) * 100;

    return {
      ...project,
      startPercentage,
      widthPercentage,
      color: segmentColors[index % segmentColors.length],
      index,
    };
  });

  const handleProjectSegmentClick = (folderPath: string) => {
    if (onProjectClick) {
      onProjectClick(folderPath);
    }
  };

  const borderColor = wouldExceedLimit
    ? 'error.main'
    : isApproachingLimit
      ? 'warning.main'
      : 'info.main';

  const tintColor = wouldExceedLimit
    ? t.status.conflict.bg
    : isApproachingLimit
      ? t.status.behind.bg
      : t.surfaces.fillSubtle;

  return (
    <Paper
      variant="panel"
      sx={{
        py: 1,
        my: 1,
        border: '2px solid',
        borderColor: borderColor,
        borderRadius: 2,
        px: 2,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: tintColor,
          borderRadius: 2,
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        mb={1}
        sx={{ position: 'relative', zIndex: 2 }}
      >
        <StorageIcon
          sx={{
            color: wouldExceedLimit
              ? 'error.main'
              : isApproachingLimit
                ? 'warning.main'
                : 'info.main',
          }}
          fontSize="small"
        />
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{
            color: wouldExceedLimit
              ? 'error.main'
              : isApproachingLimit
                ? 'warning.main'
                : 'info.main',
          }}
        >
          Storage Usage - {subscription.tier.toUpperCase()} Plan
        </Typography>
      </Box>

      {/* Combined storage bar */}
      <Box mb={1} sx={{ position: 'relative', zIndex: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2" color="text.primary">
            {storageAfterUpload > currentStorageUsed
              ? `${formatFileSize(currentStorageUsed)} → ${formatFileSize(
                  storageAfterUpload
                )}`
              : `${formatFileSize(currentStorageUsed)}`}{' '}
            of {formatFileSize(storageLimit)}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              color: wouldExceedLimit
                ? 'error.main'
                : isApproachingLimit
                  ? 'warning.main'
                  : 'primary.main',
            }}
          >
            {storagePercentageAfterUpload.toFixed(1)}%
          </Typography>
        </Box>

        {/* Single interactive bar */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 18,
            borderRadius: 1.5,
            overflow: 'hidden',
            backgroundColor: 'studio.surfaces.track',
            cursor: selectedProjects.length > 0 ? 'pointer' : 'default',
          }}
        >
          {/* Current storage (existing) */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${Math.min(currentPercentage, 100)}%`,
              backgroundColor: 'primary.main',
              opacity: 0.7,
            }}
          />

          {/* Selected project segments */}
          {projectSegments.map((segment) => {
            const leftPos = Math.min(segment.startPercentage, 100);
            const maxWidth = Math.max(0, 100 - leftPos);
            const actualWidth = Math.min(segment.widthPercentage, maxWidth);

            if (actualWidth <= 0 || leftPos >= 100) {
              return null;
            }

            return (
              <Tooltip
                key={segment.folderPath}
                title={`${segment.projectName} - ${formatFileSize(
                  segment.totalSize
                )}`}
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${leftPos}%`,
                    top: 0,
                    height: '100%',
                    width: `${actualWidth}%`,
                    backgroundColor: segment.color,
                    cursor: 'pointer',
                    borderRight:
                      segment.index < projectSegments.length - 1 &&
                      leftPos + actualWidth < 100
                        ? `1px solid ${t.borders.control}`
                        : 'none',
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      opacity: 0.9,
                      zIndex: 10,
                      filter: 'brightness(1.1)',
                    },
                  }}
                  onClick={() => handleProjectSegmentClick(segment.folderPath)}
                />
              </Tooltip>
            );
          })}

          {/* Available space (after upload) */}
          {availablePercentage > 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: `${afterPercentage}%`,
                top: 0,
                height: '100%',
                width: `${availablePercentage}%`,
                backgroundColor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {availablePercentage > 5 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 'medium',
                  }}
                >
                  {formatFileSize(
                    Math.max(0, storageLimit - storageAfterUpload)
                  )}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="space-between" mt={0.25}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize="0.7rem"
          >
            {totalSelectedSize > 0 && (
              <>Selected: {formatFileSize(totalSelectedSize)} • </>
            )}
            {formatFileSize(Math.max(0, availableStorage - totalSelectedSize))}{' '}
            available
          </Typography>
        </Box>
      </Box>

      {/* Warning messages */}
      {wouldExceedLimit && (
        <Box mt={1} sx={{ position: 'relative', zIndex: 2 }}>
          <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
            <WarningIcon sx={{ color: 'error.main' }} fontSize="small" />
            <Typography
              variant="body2"
              sx={{ color: 'error.main' }}
              fontWeight="medium"
              fontSize="0.8rem"
            >
              Upload exceeds available storage by{' '}
              {formatFileSize(totalSelectedSize - availableStorage)}
            </Typography>
          </Box>
          <Box mt={0.75}>
            <Link to="/profile/subscription">
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ textTransform: 'none', py: 0.5 }}
              >
                Upgrade Storage Plan
              </Button>
            </Link>
          </Box>
        </Box>
      )}
      {isApproachingLimit && !wouldExceedLimit && (
        <Box mt={1} sx={{ position: 'relative', zIndex: 2 }}>
          <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
            <WarningIcon sx={{ color: 'warning.main' }} fontSize="small" />
            <Typography
              variant="body2"
              sx={{ color: 'warning.main' }}
              fontWeight="medium"
              fontSize="0.8rem"
            >
              Upload will use {storagePercentageAfterUpload.toFixed(1)}% of your
              storage limit
            </Typography>
          </Box>
          <Box mt={0.75}>
            <Link to="/profile/subscription">
              <Button
                variant="outlined"
                color="warning"
                size="small"
                sx={{ textTransform: 'none', py: 0.5 }}
              >
                View Storage Plan
              </Button>
            </Link>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default StorageUsageBanner;
